import type { ApiConfig, AuthenticatedApiService } from '../types/apiType';
import { useCompanyStore } from '../store/companyStore';
import { resolveBffRoleHeader } from '../utils/resolveEffectiveRole';

import { AdhesionProposalService, AuthService, CepService, ContractsService, DashboardPaymentService, DrfService, EnumService, MaintenanceProponentService, MaintenanceSeguradoService, LoginService, MessageService, UserkeyMaintenanceService, PaymentService, ParticipantService, SubCompanyService, AdhesionService, DigitalInvoicingService, RegisterPolicyholderService, EntraIdAuthService, ProtocolService, SubEstipulanteService, PortalEstipulanteService } from './index';
import { getEnvMode, getEnvApiUrl } from './env';

// Service class for handling API requests with token-based authentication

export class ApiService {
  private baseURL: string;

  public login: LoginService;
  public userkeyMaintenance: UserkeyMaintenanceService;
  public auth: AuthService;
  public policyholder: RegisterPolicyholderService;
  public payment: PaymentService;
  public dashboardPayment: DashboardPaymentService;
  public contracts: ContractsService;
  public adhesionProposal: AdhesionProposalService;
  public protocol: ProtocolService;
  public subEstipulante: SubEstipulanteService;
  public maintenanceProponent: MaintenanceProponentService;
  public maintenanceSegurado: MaintenanceSeguradoService;
  public enum: EnumService;
  public participant: ParticipantService;
  public subCompany: SubCompanyService;
  public adhesion: AdhesionService;
  public digitalInvoicing: DigitalInvoicingService;
  public entraIdAuth: EntraIdAuthService;
  public cep: CepService;
  public drf: DrfService;
  public portalEstipulante: PortalEstipulanteService;

  constructor(config: ApiConfig) {
    this.baseURL = config.baseURL;

    // Initialize service instances with the fetch method
    this.login = new LoginService(this.fetch.bind(this));
    this.userkeyMaintenance = new UserkeyMaintenanceService(this.fetch.bind(this));
    this.auth = new AuthService(this.fetch.bind(this));
    this.policyholder = new RegisterPolicyholderService(this.fetch.bind(this));
    this.payment = new PaymentService(this.fetch.bind(this));
    this.dashboardPayment = new DashboardPaymentService(this.fetch.bind(this));
    this.contracts = new ContractsService(this.fetch.bind(this));
    this.adhesionProposal = new AdhesionProposalService(this.fetch.bind(this));
    this.protocol = new ProtocolService(this.fetch.bind(this));
    this.subEstipulante = new SubEstipulanteService(this.fetch.bind(this));
    this.maintenanceProponent = new MaintenanceProponentService(this.fetch.bind(this));
    this.maintenanceSegurado = new MaintenanceSeguradoService(this.fetch.bind(this));
    this.enum = new EnumService(this.fetch.bind(this));
    this.participant = new ParticipantService(this.fetch.bind(this));
    this.subCompany = new SubCompanyService(this.fetch.bind(this));
    this.adhesion = new AdhesionService(this.fetch.bind(this));
    this.digitalInvoicing = new DigitalInvoicingService(this.fetch.bind(this));
    this.entraIdAuth = new EntraIdAuthService(this.fetch.bind(this));
    this.cep = new CepService(this.fetch.bind(this));
    this.drf = new DrfService(this.fetch.bind(this));
    this.portalEstipulante = new PortalEstipulanteService(this.fetch.bind(this));
  }

  getBaseURL(): string {
    return this.baseURL;
  }

  setOnUnauthorized(callback: () => void) {
    // Unauthorized callbacks are intentionally disabled.
    void callback;
  }



  private async handleErrorResponse(response: Response, route?: string): Promise<never> {
    let errorMessage = 'Erro na requisição';
    let errorData: unknown = null;


    try {
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
        const errorObj = errorData as { message?: string; error?: string };
        errorMessage = errorObj.message || errorObj.error || 'Erro na requisição';
      } else {
        // If not JSON, try to read as text
        const text = await response.text();
        // Check if it's HTML or another invalid format
        if (text.includes('<!DOCTYPE') || text.includes('<html')) {
          errorMessage = 'O servidor não está respondendo, tente novamente mais tarde';
        } else {
          errorMessage = text;
        }
      }
    } catch {
      errorMessage = 'Erro ao processar resposta do servidor';
    }

    // Message override by route+status.
    try {
      if (MessageService && typeof MessageService.getMappedErrorMessage === 'function') {
        const mapped = MessageService.getMappedErrorMessage(route || '', response.status);
        if (mapped != null && mapped !== '') {
          errorMessage = mapped;
        }
      }
    } catch {
      // Keep parsed server error message when message mapping fails unexpectedly.
    }


    if (response.status === 401) {
      throw new Error(errorMessage);
    }

    if (response.status === 403) {
      throw new Error(errorMessage);
    }

    if (response.status === 400) {
      const error = new Error(errorMessage) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }

    throw new Error(errorMessage);
  }

  private resolveRoleHeader(): string {
    return resolveBffRoleHeader(useCompanyStore.getState());
  }

  public async fetch<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const roleHeader = this.resolveRoleHeader();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Role': roleHeader,
      ...(options.headers as Record<string, string> || {}),
    };

    // Add Authorization header if token is available
    // if (this.token) {
    //   headers['Authorization'] = `Bearer ${this.token}`;
    // }


    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds timeout

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include', // Important for HTTP-only cookies
        signal: controller.signal,
      });

      // Clear timeout if request completes successfully
      clearTimeout(timeoutId);


      if (!response.ok) {
        return this.handleErrorResponse(response, endpoint);
      }

      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        const jsonData = await response.json();
        return jsonData;
      } else {
        const textData = await response.text();

        // If response is empty but status is 200, consider it successful
        if (response.status === 200 && textData === '') {
          return { success: true } as T;
        }

        throw new Error('O servidor não está respondendo, tente novamente');
      }
    } catch (error) {
      // Clear timeout in case of error
      clearTimeout(timeoutId);


      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Tempo limite da requisição excedido');
        }
        if (error.message === 'Failed to fetch') {
          throw new Error('Não foi possível conectar ao servidor, tente novamente');
        }
        throw error;
      }
      throw new Error('Não foi possível conectar ao servidor, tente novamente');
    }
  }
}

// Environment configuration that can be mocked in tests
export const ENV_CONFIG = {
  getMode: (): string => {
    try {
      return getEnvMode();
    } catch {
      return 'production';
    }
  },
  getApiUrl: (): string => {
    try {
      return getEnvApiUrl();
    } catch {
      // Fallback para produção ao invés de localhost
      return 'http://localhost:8070/cadp-bff-estipulante';
    }
  },
};

// Helper function to get base URL for API requests
// Exported for testing purposes
export const getBaseURL = (): string => {
  const apiUrl = ENV_CONFIG.getApiUrl();

  // Always use the fixed API URL
  return apiUrl;
};

// Creating a service instance with environment variables
const baseURL = getBaseURL();

export const apiService = new ApiService({
  baseURL,
}) as AuthenticatedApiService;
